import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { BookOpen, PlayCircle, CheckCircle, Lock, ChevronRight, Calculator, Globe, FlaskConical } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const iconMap = {
  calculator: Calculator,
  "book-open": BookOpen,
  "flask-conical": FlaskConical,
  globe: Globe,
};

export default function ProgramView() {
  const { programId } = useParams();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState({});
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [progRes, modsRes, progProgressRes] = await Promise.all([
          fetch(`${API}/programs/${programId}`, { credentials: "include" }),
          fetch(`${API}/programs/${programId}/modules`, { credentials: "include" }),
          fetch(`${API}/programs/${programId}/progress`, { credentials: "include" }),
        ]);
        if (progRes.ok) setProgram(await progRes.json());
        if (modsRes.ok) {
          const mods = await modsRes.json();
          setModules(mods);
          const lessonPromises = mods.map(m =>
            fetch(`${API}/programs/${programId}/modules/${m.module_id}/lessons`, { credentials: "include" }).then(r => r.json())
          );
          const allLessons = await Promise.all(lessonPromises);
          const lessonsMap = {};
          mods.forEach((m, i) => { lessonsMap[m.module_id] = allLessons[i]; });
          setLessons(lessonsMap);
        }
        if (progProgressRes.ok) setProgress(await progProgressRes.json());
      } catch {}
      setLoading(false);
    };
    load();
  }, [programId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-4 border-[#20D3C0] border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  const progressPct = progress ? (progress.total_lessons > 0 ? Math.round((progress.completed_lessons / progress.total_lessons) * 100) : 0) : 0;
  const isEnrolled = user?.program === programId;
  const isActive = user?.subscription_status === "active";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="program-view">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <img src={program?.image} alt={program?.name} className="w-full md:w-48 h-32 rounded-xl object-cover" />
            <div className="flex-1">
              <span className="text-xs font-semibold text-[#20D3C0] bg-[#20D3C0]/10 px-3 py-1 rounded-full">
                {program?.institution}
              </span>
              <h1 className="text-3xl font-bold text-[#193B6E] mt-3 mb-2" style={{ fontFamily: 'Outfit' }} data-testid="program-title">
                {program?.name}
              </h1>
              <p className="text-slate-500 text-sm">{program?.description}</p>
              {isEnrolled && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-slate-500">Progreso general</span>
                    <span className="text-sm font-semibold text-[#193B6E]">{progressPct}%</span>
                  </div>
                  <Progress value={progressPct} className="h-2" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modules */}
        <Tabs defaultValue={modules[0]?.module_id} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 rounded-xl flex-wrap h-auto">
            {modules.map((mod) => {
              const Icon = iconMap[mod.icon] || BookOpen;
              return (
                <TabsTrigger
                  key={mod.module_id}
                  value={mod.module_id}
                  className="flex items-center gap-2 data-[state=active]:bg-[#20D3C0]/10 data-[state=active]:text-[#193B6E] rounded-lg px-4 py-2"
                  data-testid={`module-tab-${mod.module_id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{mod.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {modules.map((mod) => (
            <TabsContent key={mod.module_id} value={mod.module_id}>
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="p-6 border-b border-slate-100">
                  <h3 className="text-xl font-semibold text-[#193B6E]" style={{ fontFamily: 'Outfit' }}>{mod.name}</h3>
                  <p className="text-sm text-slate-500 mt-1">{mod.description}</p>
                </div>
                <div className="divide-y divide-slate-100">
                  {(lessons[mod.module_id] || []).map((lesson) => {
                    const isCompleted = progress?.progress?.[lesson.lesson_id]?.completed;
                    const canAccess = lesson.is_free || (isEnrolled && isActive);
                    return (
                      <div key={lesson.lesson_id} className="flex items-center gap-4 p-4 hover:bg-[#F8FAFC] transition-colors" data-testid={`lesson-${lesson.lesson_id}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCompleted ? 'bg-green-100 text-green-500' : canAccess ? 'bg-[#20D3C0]/10 text-[#20D3C0]' : 'bg-slate-100 text-slate-400'}`}>
                          {isCompleted ? <CheckCircle className="w-5 h-5" /> : canAccess ? <PlayCircle className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#193B6E] truncate">{lesson.title}</p>
                          <p className="text-xs text-slate-400">{lesson.duration_minutes} min</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {lesson.is_free && (
                            <span className="text-xs font-semibold text-green-500 bg-green-50 px-2 py-1 rounded-full">Gratis</span>
                          )}
                          {canAccess ? (
                            <Link
                              to={`/program/${programId}/lesson/${lesson.lesson_id}`}
                              className="text-[#20D3C0] hover:text-[#1AB8A6]"
                              data-testid={`go-lesson-${lesson.lesson_id}`}
                            >
                              <ChevronRight className="w-5 h-5" />
                            </Link>
                          ) : (
                            <Link to="/payment" className="text-xs text-[#20D3C0] font-medium hover:underline">
                              Desbloquear
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
